import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';

const EnhancedPerformanceDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedQuizzes, setSelectedQuizzes] = useState([]);
  const [filters, setFilters] = useState({
    searchTitle: '',
    courseId: user?.role === 'teacher' ? '' : '', // Teachers don't need course filter since backend filters automatically
    status: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'title',
    sortOrder: 'ASC',
    page: 1,
    limit: 20
  });

  // Helper function to get the correct navigation path based on user role
  const getQuizDetailsPath = (quizId) => {
    const basePath = user?.role === 'admin' 
      ? '/admin/dashboard/performance/quiz-details/' 
      : '/teachers/dashboard/performance/quiz-details/';
    return `${basePath}${quizId}`;
  };

  const [courses, setCourses] = useState([]);

  useEffect(() => {
    // Load courses for filter dropdown (only for admins)
    const fetchCourses = async () => {
      // Teachers don't need course dropdown since backend automatically filters their courses
      if (user?.role === 'teacher') {
        setCourses([]);
        return;
      }
      
      try {
        const response = await api.get('/courses', { params: { limit: 100 } });
        setCourses(response.data.data || response.data);
      } catch (error) {
        console.error('Error fetching courses:', error);
        setCourses([]);
      }
    };
    
    fetchCourses();
  }, [user?.role]);

  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // For teachers, remove courseId from filters since backend automatically filters by teacherId
      const apiFilters = { ...filters };
      if (user?.role === 'teacher') {
        delete apiFilters.courseId;
      }
      
      const response = await api.get('/performance/dashboard', { params: apiFilters });
      setData(response.data.data || []);
      setSummary(response.data.summary || {});
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handleSort = (column) => {
    const newOrder = filters.sortBy === column && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setFilters(prev => ({
      ...prev,
      sortBy: column,
      sortOrder: newOrder
    }));
  };

  const handleSelectQuiz = (quizId) => {
    setSelectedQuizzes(prev => 
      prev.includes(quizId) 
        ? prev.filter(id => id !== quizId)
        : [...prev, quizId]
    );
  };

  const handleSelectAll = () => {
    if (selectedQuizzes.length === data.length) {
      setSelectedQuizzes([]);
    } else {
      setSelectedQuizzes(data.map(quiz => quiz.quizId));
    }
  };

  const handleExport = async (format = 'csv', includeStudentDetails = false) => {
    if (selectedQuizzes.length === 0) {
      alert('Please select at least one quiz to export');
      return;
    }

    try {
      const params = {
        quizIds: selectedQuizzes.join(','),
        format,
        includeStudentDetails
      };

      if (format === 'csv') {
        const response = await api.get('/performance/export', { 
          params,
          responseType: 'blob'
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'quiz_performance.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const response = await api.get('/performance/export', { params });
        console.log('Export data:', response.data);
        // Handle JSON export (could save to file or display)
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-50 text-green-700 border border-green-200',
      expired: 'bg-red-50 text-red-700 border border-red-200',
      upcoming: 'bg-blue-50 text-blue-700 border border-blue-200'
    };
    return `inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${colors[status] || 'bg-gray-50 text-gray-700 border border-gray-200'}`;
  };

  const getPerformanceBadge = (rating) => {
    const colors = {
      high: 'bg-green-50 text-green-700 border border-green-200',
      medium: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
      low: 'bg-red-50 text-red-700 border border-red-200'
    };
    return {
      className: `inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${colors[rating] || 'bg-gray-50 text-gray-700 border border-gray-200'}`
    };
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
      <div className="bg-white border border-gray-200 rounded-md p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Performance Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">Quiz performance analytics and reporting</p>
          </div>
          {user?.role === 'teacher' && (
            <div className="px-3 py-1.5 rounded border border-blue-200 bg-blue-50 text-sm text-blue-700">
              Your courses only
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Total Quizzes</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{summary.totalQuizzes || 0}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Total Attempts</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{summary.totalCompletedAttempts || 0}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Average Score</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{summary.averageScore || 0}%</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">High Performance</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{summary.highPerformanceQuizzes || 0}</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label htmlFor="searchTitle" className="block text-sm font-medium text-gray-700 mb-1.5">Search quiz</label>
            <input
              id="searchTitle"
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
              placeholder="Enter quiz title..."
              value={filters.searchTitle}
              onChange={(e) => handleFilterChange('searchTitle', e.target.value)}
            />
          </div>
          {/* Only show course filter for admins - teachers see only their courses automatically */}
          {user?.role === 'admin' && (
            <div>
              <label htmlFor="courseFilter" className="block text-sm font-medium text-gray-700 mb-1.5">Course</label>
              <select
                id="courseFilter"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                value={filters.courseId}
                onChange={(e) => handleFilterChange('courseId', e.target.value)}
              >
                <option value="">All Courses</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>{course.title}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
            <select
              id="statusFilter"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="upcoming">Upcoming</option>
            </select>
          </div>
          <div>
            <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700 mb-1.5">Date from</label>
            <input
              id="dateFrom"
              type="date"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700 mb-1.5">Date to</label>
            <input
              id="dateTo"
              type="date"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Export</h2>
              <span className="text-sm text-gray-600">
                {selectedQuizzes.length} of {data.length} quizzes selected
              </span>
            </div>
            <button
              onClick={handleSelectAll}
              className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              {selectedQuizzes.length === data.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleExport('csv', false)}
              disabled={selectedQuizzes.length === 0}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Export CSV
            </button>
            <button
              onClick={() => handleExport('csv', true)}
              disabled={selectedQuizzes.length === 0}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Export with Students
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Quiz performance overview</h2>
          <p className="text-sm text-gray-600 mt-1">View and export detailed metrics for all quizzes.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedQuizzes.length === data.length && data.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                    onClick={() => handleSort('title')}>
                  <div className="flex items-center gap-1 cursor-pointer">
                    Quiz Title 
                    {filters.sortBy === 'title' && (
                      <span className="text-gray-600">{filters.sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Course</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                    onClick={() => handleSort('totalStudents')}>
                  <div className="flex items-center gap-1 cursor-pointer">
                    Students
                    {filters.sortBy === 'totalStudents' && (
                      <span className="text-gray-600">{filters.sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                    onClick={() => handleSort('avgScore')}>
                  <div className="flex items-center gap-1 cursor-pointer">
                    Avg Score
                    {filters.sortBy === 'avgScore' && (
                      <span className="text-gray-600">{filters.sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Pass Rate</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Max Attempts</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Performance</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-700"></div>
                      <p className="text-sm text-gray-600">Loading quiz data...</p>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center">
                    <div>
                      <p className="text-gray-700 font-medium">No quizzes found.</p>
                      <p className="text-gray-500 text-sm mt-1">Try adjusting your filters.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((quiz) => (
                  <tr key={quiz.quizId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedQuizzes.includes(quiz.quizId)}
                        onChange={() => handleSelectQuiz(quiz.quizId)}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{quiz.quizTitle}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(quiz.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{quiz.courseTitle}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-gray-900">{quiz.totalStudents}</span>
                        <span className="text-xs text-gray-500">students</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {quiz.completedAttempts} attempts
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="text-base font-semibold text-gray-900">{quiz.avgScore}%</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {quiz.minScore}% - {quiz.maxScore}%
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{quiz.passRate ? `${quiz.passRate}%` : 'N/A'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 bg-gray-100 rounded text-sm text-gray-700">
                        {quiz.maxAttempts || '∞'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={getStatusBadge(quiz.status)}>
                        {quiz.status.charAt(0).toUpperCase() + quiz.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const badge = getPerformanceBadge(quiz.performanceRating);
                        return (
                          <span className={badge.className}>
                            {quiz.performanceRating.charAt(0).toUpperCase() + quiz.performanceRating.slice(1)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => navigate(getQuizDetailsPath(quiz.quizId))}
                        className="px-3 py-1.5 bg-gray-800 text-white text-sm rounded hover:bg-gray-900 w-full sm:w-auto"
                        title="View detailed performance data for this quiz"
                      >
                        View details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-gray-50 px-5 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Showing <span className="font-semibold">{((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}</span> to{' '}
                <span className="font-semibold">{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}</span> of{' '}
                <span className="font-semibold">{pagination.totalItems}</span> quizzes
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                  disabled={pagination.currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <div className="px-3 py-2 bg-gray-800 text-white rounded text-sm font-medium">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </div>
                <button
                  onClick={() => handleFilterChange('page', Math.min(pagination.totalPages, filters.page + 1))}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedPerformanceDashboard;
